import {
  VoiceDetectorConstructor,
  VoiceDetector,
  VoiceDetectorOptions,
} from "@service/voice-detection/voice-detector";
import { VoiceDetectionEvent } from "./voice-detector";

const MIN_DECIBELS = -45;
const DEFAULT_PAUSE_DURATION = 1_600;
const MAX_DURATION = 20_000;

export const VolumeVoiceDetector: VoiceDetectorConstructor = class VolumeVoiceDetector
  implements VoiceDetector
{
  private stopped = true;
  private readonly audioNode: AudioNode | null = null;
  private readonly pauseDuration: number;

  constructor({ stream, callback, pauseDuration }: VoiceDetectorOptions) {
    this.pauseDuration = pauseDuration || DEFAULT_PAUSE_DURATION;
    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyzer = context.createAnalyser();
    analyzer.minDecibels = MIN_DECIBELS;
    source.connect(analyzer);
    this.startDetection(analyzer, callback);

    this.audioNode = source;
  }

  startDetection(
    analyzer: AnalyserNode,
    onEvent: (e: VoiceDetectionEvent) => void,
  ): void {
    this.stopped = false;
    const bufferLength = analyzer.frequencyBinCount;
    const domainData = new Uint8Array(bufferLength);

    let isTalking = false;
    let lastDetectedTime = Date.now();
    let startTime = Date.now();

    const detectSound = () => {
      if (this.stopped) {
        return;
      }
      analyzer.getByteFrequencyData(domainData);

      for (let i = 0; i < bufferLength; i++) {
        if (domainData[i] > 0) {
          lastDetectedTime = Date.now();

          if (!isTalking) {
            onEvent({ type: "start" });
            // No misfire filter here; collapse to a single confirmed event so
            // consumers that gate barge-in on `real-start` still work.
            onEvent({ type: "real-start" });
            isTalking = true;
            startTime = Date.now();
          }
        }
      }

      if (isTalking && Date.now() - lastDetectedTime > this.pauseDuration) {
        onEvent({ type: "end" });
        isTalking = false;
      }

      if (isTalking && Date.now() - startTime > MAX_DURATION) {
        onEvent({ type: "max_duration" });
        isTalking = false;
      }

      window.requestAnimationFrame(detectSound);
    };

    detectSound();
  }

  destroy() {
    this.stopped = true;
    if (this.audioNode) {
      this.audioNode.disconnect();
    }
  }
};
